// src/pages/SignupPage.js
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { signUp, verifyAccessCode, claimAccessCode } from '../lib/supabase'
import { useToast } from '../components/Toast'
import { useLang } from '../lib/i18n'
import Navbar from '../components/Navbar'
import SocialFooter from '../components/SocialFooter'
import './AuthPage.css'
import './SignupPage.css'

const PA_PHONE    = process.env.REACT_APP_PA_PHONE    || '+17405610387'
const PA_WHATSAPP = process.env.REACT_APP_PA_WHATSAPP || '+17572956292'
const PA_ZANGI    = process.env.REACT_APP_PA_ZANGI    || 'https://services.zangi.com/dl/conversation/1087382741'

const TIERS = [
  { id:'gold',     price:150,  label:'Gold',     subtitle:'The Admirer',        color:'var(--gold)',
    perks:['Weekly 24hr Q&A session','4 sessions per month','Exclusive vault access','Weekly personal question','Loyalty rewards & referrals'],
    locked:['Unlimited messaging','Zangi & private number','Voice calls','Private dinner'] },
  { id:'premium',  price:500,  label:'Premium',  subtitle:'The Companion',      color:'var(--plat)', featured:true,
    perks:['Everything in Gold','Unlimited chat — no timer','Zangi messaging unlocked','Private number unlocked','Premium vault content'],
    locked:['Voice calls','Private dinner'] },
  { id:'platinum', price:1000, label:'Platinum', subtitle:'The Intimate Circle',color:'var(--plat-bright)',
    perks:['Everything in Premium','Free voice calls built-in','Private dinner booking','Full vault — all content','VIP loyalty milestones'],
    locked:[] },
]

const PAYMENT_METHODS = [
  { id:'Apple Gift Card', icon:'🍎' },
  { id:'Visa Gift Card',  icon:'💳' },
  { id:'Razer Gold',      icon:'🎮' },
  { id:'CashApp',         icon:'💚' },
]

// steps: 1=choose tier, 2=payment+PA contact, 3=enter code, 4=create account, 5=email confirm
export default function SignupPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const refCode  = params.get('ref') || ''
  const toast    = useToast()
  const { t }    = useLang()

  const [step, setStep]               = useState(1)
  const [selectedTier, setSelectedTier] = useState(null)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [codeInput, setCodeInput]     = useState(params.get('code') || '')
  const [codeData, setCodeData]       = useState(null)
  const [verifying, setVerifying]     = useState(false)
  const [form, setForm] = useState({ displayName:'', email:'', password:'', confirm:'', birthday:'' })
  const [showPass, setShowPass] = useState(false)
  const [showConf, setShowConf] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [copiedPA, setCopiedPA] = useState(false)

  function handleChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })) }

  async function handleVerifyCode(e) {
    e.preventDefault()
    if (!codeInput.trim()) return
    setVerifying(true)
    const { valid, data, error } = await verifyAccessCode(codeInput.trim())
    setVerifying(false)
    if (!valid) { toast.show(error || 'Invalid or already used code', 'error'); return }
    setCodeData(data)
    setStep(4)
    toast.show(`${data.tier.charAt(0).toUpperCase()+data.tier.slice(1)} access unlocked! ✓`, 'success')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.displayName.trim()) { toast.show('Enter your name', 'error'); return }
    if (form.password.length < 8)  { toast.show('Password must be at least 8 characters', 'error'); return }
    if (form.password !== form.confirm) { toast.show('Passwords do not match', 'error'); return }
    setLoading(true)
    const { data: authData, error } = await signUp({
      email: form.email, password: form.password,
      displayName: form.displayName, tier: codeData.tier,
      birthday: form.birthday || null, refCode
    })
    if (error) { setLoading(false); toast.show(error.message || 'Something went wrong', 'error'); return }
    const userId = authData?.user?.id
    if (userId) await claimAccessCode(codeInput.trim(), userId)
    setLoading(false)
    setStep(5)
  }

  async function copyPANumber() {
    try { await navigator.clipboard.writeText(PA_PHONE) } catch {}
    setCopiedPA(true); setTimeout(() => setCopiedPA(false), 2500)
  }

  return (
    <div className="auth-page">
      <Navbar />
      <div className="auth-wrap" style={{alignItems:'flex-start', paddingTop:40}}>
        <div className="auth-box auth-box--wide">

          {/* STEP 1 — Choose tier */}
          {step === 1 && (
            <>
              <div className="auth-box__header">
                <p className="auth-box__eyebrow">Join the Inner Circle</p>
                <h1 className="auth-box__title">Choose Your Card</h1>
              </div>
              <div className="signup-warning">
                ⚠️ Only trust contact information listed on this site as the official source to purchase your Membership Card.
              </div>
              <div className="tier-grid">
                {TIERS.map(tier => (
                  <div key={tier.id} className={`tier-opt ${tier.featured ? 'tier-opt--featured' : ''}`} onClick={() => { setSelectedTier(tier); setStep(2) }}>
                    {tier.featured && <div className="tier-opt__badge">Most Popular</div>}
                    <span className={`badge badge--${tier.id}`}>Tier {TIERS.indexOf(tier)+1}</span>
                    <h3 className="tier-opt__name" style={{color:tier.color}}>{tier.label}</h3>
                    <p className="tier-opt__sub">{tier.subtitle}</p>
                    <p className="tier-opt__price"><span style={{fontFamily:'Cormorant Garamond,serif',fontSize:28,color:'var(--cream)'}}>${tier.price}</span> <span style={{fontSize:11,color:'var(--muted)'}}>one-time</span></p>
                    <ul className="tier-opt__perks">
                      {tier.perks.map((p,i) => <li key={i} className="tier-opt__perk tier-opt__perk--on"><span className="tier-opt__dot tier-opt__dot--on"/>{ p}</li>)}
                      {tier.locked.map((p,i) => <li key={i} className="tier-opt__perk tier-opt__perk--off"><span className="tier-opt__dot tier-opt__dot--off"/>{p}</li>)}
                    </ul>
                    <button className={`btn btn--full ${tier.id==='gold'?'btn--outline':tier.id==='platinum'?'btn--plat':'btn--gold'}`} style={{marginTop:8}}
                      onClick={e => {e.stopPropagation(); setSelectedTier(tier); setStep(2)}}>
                      Select {tier.label} — ${tier.price}
                    </button>
                  </div>
                ))}
              </div>
              <p className="auth-switch">{t('alreadyMember')} <Link to="/login">{t('signIn')}</Link></p>
            </>
          )}

          {/* STEP 2 — Payment + PA Contact */}
          {step === 2 && selectedTier && (
            <>
              <div className="auth-box__header">
                <p className="auth-box__eyebrow" style={{color:selectedTier.color}}>{selectedTier.label} — ${selectedTier.price}</p>
                <h1 className="auth-box__title">How Would You Like to Pay?</h1>
                <p className="auth-box__sub">Choose a payment method, then contact Sky Bri's Team to complete your purchase.</p>
              </div>
              <div className="payment-grid">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.id}
                    className={`payment-btn ${selectedPayment===pm.id?'payment-btn--active':''}`}
                    onClick={() => setSelectedPayment(pm.id)}>
                    <span style={{fontSize:22}}>{pm.icon}</span>
                    <span>{pm.id}</span>
                  </button>
                ))}
              </div>

              {selectedPayment && (
                <div className="pa-contact-box">
                  <p className="pa-contact-box__title">Contact Sky Bri's Team to pay via {selectedPayment}</p>
                  <p className="pa-contact-box__sub">Mention your chosen tier: <strong style={{color:selectedTier.color}}>{selectedTier.label} (${selectedTier.price})</strong></p>
                  <div className="pa-contact-box__methods">
                    <a href={`sms:${PA_PHONE}`} className="pa-method">
                      <span>💬</span>
                      <div><p className="pa-method__label">Text Message</p><p className="pa-method__val">{PA_PHONE}</p></div>
                      <span className="pa-method__action">Text →</span>
                    </a>
                    <a href={`https://wa.me/${PA_WHATSAPP.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="pa-method">
                      <span>📱</span>
                      <div><p className="pa-method__label">WhatsApp</p><p className="pa-method__val">{PA_WHATSAPP}</p></div>
                      <span className="pa-method__action">Chat →</span>
                    </a>
                    <a href={PA_ZANGI} target="_blank" rel="noreferrer" className="pa-method">
                      <span>💬</span>
                      <div><p className="pa-method__label">Zangi</p><p className="pa-method__val">Sky Bri's Team</p></div>
                      <span className="pa-method__action">Open →</span>
                    </a>
                    <button className="pa-method pa-method--copy" onClick={copyPANumber}>
                      <span>📋</span>
                      <div><p className="pa-method__label">{copiedPA ? '✓ Copied!' : 'Copy Number'}</p><p className="pa-method__val">{PA_PHONE}</p></div>
                    </button>
                  </div>
                  <p className="pa-contact-box__note">🕐 Available Mon–Fri · Codes delivered within 24 hours of payment confirmation</p>
                  <p className="pa-contact-box__warning">⚠️ Only trust contact info on this site as the official source to purchase your Membership Card.</p>
                </div>
              )}

              <div className="signup-step-nav">
                <button className="btn btn--ghost btn--sm" onClick={() => setStep(1)}>← Back</button>
                <button className="btn btn--gold" onClick={() => setStep(3)}>I Have My Code →</button>
              </div>
              <p className="auth-switch">{t('alreadyMember')} <Link to="/login">{t('signIn')}</Link></p>
            </>
          )}

          {/* STEP 3 — Enter Code */}
          {step === 3 && (
            <>
              <div className="auth-box__header">
                <p className="auth-box__eyebrow">Private Access</p>
                <h1 className="auth-box__title">Enter Your Code</h1>
                <p className="auth-box__sub">Enter the unique code you received from Sky Bri's Team after payment.</p>
              </div>
              <form onSubmit={handleVerifyCode}>
                <div className="field">
                  <label>Access Code</label>
                  <input type="text" placeholder="SKY-GOLD-XXXX" value={codeInput}
                    onChange={e => setCodeInput(e.target.value.toUpperCase())} required autoComplete="off"
                    style={{letterSpacing:3, fontFamily:'monospace', fontSize:18, textAlign:'center'}} />
                </div>
                <button className="btn btn--gold btn--full" type="submit" disabled={verifying}>
                  {verifying ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>
              <div className="signup-step-nav" style={{marginTop:16}}>
                <button className="btn btn--ghost btn--sm" onClick={() => setStep(2)}>← Back</button>
              </div>
              <p className="auth-switch">{t('alreadyMember')} <Link to="/login">{t('signIn')}</Link></p>
            </>
          )}

          {/* STEP 4 — Create Account */}
          {step === 4 && codeData && (
            <>
              <div className="auth-box__header">
                <p className="auth-box__eyebrow" style={{color:'#4CAF7D'}}>Code Verified ✓</p>
                <h1 className="auth-box__title">Create Your Account</h1>
                <p className="auth-box__sub">Complete your profile to activate your Lover's Card</p>
              </div>
              <div className={`tier-preview tier-preview--${codeData.tier}`}>
                <span className={`badge badge--${codeData.tier}`}>{codeData.tier.charAt(0).toUpperCase()+codeData.tier.slice(1)} Card Activated</span>
                {codeData.payment_method && <p style={{fontSize:12,color:'var(--muted)',marginTop:4}}>Paid via {codeData.payment_method}{codeData.amount_paid ? ` · $${codeData.amount_paid}` : ''}</p>}
              </div>
              <form onSubmit={handleSubmit}>
                <div className="field"><label>Your Name</label>
                  <input name="displayName" type="text" placeholder="How should Sky Bri address you?"
                    value={form.displayName} onChange={handleChange} required /></div>
                <div className="field"><label>Email Address</label>
                  <input name="email" type="email" placeholder="your@email.com"
                    value={form.email} onChange={handleChange} required autoComplete="email" /></div>
                <div className="field">
                  <label>Birthday <span style={{color:'var(--dim)',fontSize:11}}>(optional — for a special surprise)</span></label>
                  <input name="birthday" type="date" value={form.birthday} onChange={handleChange} /></div>
                <div className="field"><label>Password</label>
                  <div className="pass-wrap">
                    <input name="password" type={showPass?'text':'password'} placeholder="Minimum 8 characters"
                      value={form.password} onChange={handleChange} required autoComplete="new-password" />
                    <button type="button" className="pass-toggle" onClick={()=>setShowPass(v=>!v)}>{showPass?'🙈':'👁'}</button>
                  </div></div>
                <div className="field"><label>Confirm Password</label>
                  <div className="pass-wrap">
                    <input name="confirm" type={showConf?'text':'password'} placeholder="Repeat your password"
                      value={form.confirm} onChange={handleChange} required autoComplete="new-password" />
                    <button type="button" className="pass-toggle" onClick={()=>setShowConf(v=>!v)}>{showConf?'🙈':'👁'}</button>
                  </div></div>
                <button className={`btn btn--full ${codeData.tier==='gold'?'btn--outline':codeData.tier==='platinum'?'btn--plat':'btn--gold'}`}
                  type="submit" disabled={loading} style={{marginTop:8}}>
                  {loading ? 'Activating...' : `Activate My ${codeData.tier.charAt(0).toUpperCase()+codeData.tier.slice(1)} Card`}
                </button>
              </form>
              <button onClick={() => setStep(3)}
                style={{background:'none',border:'none',cursor:'pointer',width:'100%',marginTop:16,color:'var(--muted)',fontSize:13,fontFamily:"'Jost',sans-serif"}}>
                ← Use a different code
              </button>
            </>
          )}

          {/* STEP 5 — Email Confirmation */}
          {step === 5 && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',padding:'8px 0 16px',gap:16}}>
              <div style={{fontSize:52}}>✉️</div>
              <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:28,fontWeight:300,color:'var(--cream)'}}>Check Your Email</h2>
              <p style={{fontSize:14,color:'var(--muted)',lineHeight:1.7}}>We sent a confirmation link to <strong style={{color:'var(--cream)'}}>{form.email}</strong></p>
              <div style={{padding:'16px 20px',background:'var(--bg-surface)',border:'0.5px solid var(--border)',textAlign:'left',width:'100%',fontSize:13,color:'var(--muted)',lineHeight:2}}>
                1. Open your email inbox<br/>
                2. Find the email from <strong style={{color:'var(--cream)'}}>Sky Bri Private</strong><br/>
                3. Click the confirmation link<br/>
                4. Return here and log in with your email and password
              </div>
              <p style={{fontSize:12,color:'var(--dim)'}}>Didn't receive it? Check your spam folder.</p>
              <Link to="/login" className="btn btn--gold btn--full" style={{display:'block',textAlign:'center',marginTop:8}}>Go to Login →</Link>
            </div>
          )}

        </div>
      </div>
      <SocialFooter />
    </div>
  )
}
